import React from "react";
import Input from "../../../components/Atomos/Inputs/Input";
import Dropdown from "../../../components/Atomos/DropDown/Dropdown";
import DDMultiple from "../../../components/Atomos/DropDown/DDMultiple";
import LegacyButton from "../../../components/Atomos/Buttons/LegacyButton";
import ChevronRight from "../../../components/Atomos/Icons/ChevronRight";
import SearchIcon from "../../../components/Atomos/Icons/Search";
import RadioGroup from "../../SendEmail/components/RadioGroup";
import {
  MEMBER_SEARCH_PROFESSIONAL_TITLE_OPTIONS,
  MEMBER_SEARCH_SERVICE_TYPE_OPTIONS,
  MEMBER_SEARCH_VALIDATION_FIELDS_FIRM,
  MEMBER_SEARCH_VALIDATION_FIELDS_PROFESSIONAL,
  listMemberSearchLocations,
  listMemberSearchRegions,
  type MemberSearchCriteria,
  type MemberSearchOption,
  type MemberSearchServiceTypeFilter,
  type MemberSearchWithin,
} from "../memberSearchQuery";

type DropdownOption = MemberSearchOption;

type AdvancedMemberSearchFormProps = {
  criteria: MemberSearchCriteria;
  onChange: (patch: Partial<MemberSearchCriteria>) => void;
  onSearch: () => void;
  showValidation: boolean;
  firms: DropdownOption[];
  regions: DropdownOption[];
  locations: DropdownOption[];
  practiceIndustries: DropdownOption[];
  officeLocations: DropdownOption[];
  officeLocationsLoading?: boolean;
};

const SEARCH_WITHIN_OPTIONS: Array<{ value: MemberSearchWithin; label: string }> = [
  { value: "firm", label: "Firm" },
  { value: "professional", label: "Professional" },
];

const SERVICE_TYPE_OPTIONS: Array<{
  value: MemberSearchServiceTypeFilter;
  label: string;
}> = MEMBER_SEARCH_SERVICE_TYPE_OPTIONS.map((option) => ({
  value: option.id as MemberSearchServiceTypeFilter,
  label: option.name,
}));

const KeywordField: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => (
  <div className="relative w-full">
    <Input
      title=""
      placeholder="Keyword"
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={false}
      className="pr-10"
    />
    <span className="pointer-events-none absolute right-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-gray-400">
      <SearchIcon />
    </span>
  </div>
);

const AdvancedMemberSearchForm: React.FC<AdvancedMemberSearchFormProps> = ({
  criteria,
  onChange,
  onSearch,
  showValidation,
  firms,
  regions,
  locations,
  practiceIndustries,
  officeLocations,
  officeLocationsLoading = false,
}) => {
  const searchWithin: MemberSearchWithin = criteria.searchWithin ?? "firm";
  const isProfessional = searchWithin === "professional";
  const validationFields = isProfessional
    ? MEMBER_SEARCH_VALIDATION_FIELDS_PROFESSIONAL
    : MEMBER_SEARCH_VALIDATION_FIELDS_FIRM;
  const selectedServiceType = criteria.serviceType ?? "all";
  const selectedRegions = listMemberSearchRegions(criteria);
  const selectedLocations = listMemberSearchLocations(criteria);
  const hasFirm = Boolean(criteria.firm?.id?.trim());
  const hasRegions = selectedRegions.length > 0;
  const hasLocations = selectedLocations.length > 0;
  const officeFieldPlaceholder = officeLocationsLoading
    ? "Loading office locations..."
    : "Select Office Location";
  const officeDisabled = officeLocationsLoading && officeLocations.length === 0;

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSearch();
  };

  const handleFirmSelect = (option: DropdownOption) => {
    const firmId = option?.id?.trim();
    if (!firmId) {
      onChange({ firm: undefined, officeLocation: undefined });
      return;
    }
    onChange({
      firm: option,
      regions: [],
      locations: [],
    });
  };

  const toMemberOptions = (
    options: Array<{ id?: string; name: string }>
  ): MemberSearchOption[] =>
    options.flatMap((option) => {
      const id = option.id?.trim();
      if (!id) {
        return [];
      }
      return [{ id, name: option.name }];
    });

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full flex-col rounded-md shadow-[0px_2px_8px_rgba(31,31,31,0.12),0px_1px_8px_rgba(31,31,31,0.06)]"
    >
      <div className="flex items-center justify-between rounded-t-md bg-accent-Default-dark px-8 py-[22px]">
        <h1 className="font-roboto text-2xl font-bold uppercase tracking-wide text-white">
          Advanced Member Search
        </h1>
        <button
          type="button"
          className="flex h-6 w-6 items-center justify-center rounded-full bg-Default text-sm font-bold text-white"
          aria-label="Help"
        >
          ?
        </button>
      </div>

      <div className="flex flex-col gap-6 rounded-b-md bg-white p-8">
        {showValidation ? (
          <div className="flex flex-col gap-4 text-lg text-primary">
            <p>Please, enter at least one of the following fields:</p>
            <ul className="grid grid-cols-1 gap-2 font-medium sm:grid-cols-2">
              {validationFields.map((field) => (
                <li key={field}>• {field}</li>
              ))}
            </ul>
          </div>
        ) : null}

        <RadioGroup
          title="Search Within:"
          name="searchWithin"
          color="accent"
          value={searchWithin}
          options={SEARCH_WITHIN_OPTIONS}
          onChange={(value) =>
            onChange({
              searchWithin: value,
              ...(value === "professional"
                ? {
                    region: undefined,
                    regions: [],
                    location: undefined,
                    locations: [],
                    serviceType: undefined,
                    professionalTitles: [],
                    professionalTitle: undefined,
                  }
                : {
                    lastName: "",
                    professionalTitles: undefined,
                    professionalTitle: undefined,
                  }),
            })
          }
        />

        {isProfessional ? (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Input
                title=""
                placeholder="Last Name"
                value={criteria.lastName}
                onChange={(event) => onChange({ lastName: event.target.value })}
                error={false}
              />
              <KeywordField
                value={criteria.keyword}
                onChange={(keyword) => onChange({ keyword })}
              />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Dropdown
                title=""
                placeholder="Select Firm"
                data={firms}
                selectedObjst={criteria.firm?.name}
                seter={handleFirmSelect}
                getid
                internalSearch
                renderMenuInPortal
                maxHeight="max-h-120"
              />
              <Dropdown
                key={criteria.firm?.id ?? "all-firms"}
                title=""
                placeholder={officeFieldPlaceholder}
                data={officeLocations}
                selectedObjst={criteria.officeLocation?.name}
                seter={(option: DropdownOption) =>
                  onChange({ officeLocation: option })
                }
                getid
                internalSearch
                renderMenuInPortal
                maxHeight="max-h-120"
                disabled={officeDisabled}
              />
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Dropdown
                title=""
                placeholder="Select Practice Industry"
                data={practiceIndustries}
                selectedObjst={criteria.practiceIndustry?.name}
                seter={(option: DropdownOption) =>
                  onChange({ practiceIndustry: option })
                }
                getid
                internalSearch
                renderMenuInPortal
                maxHeight="max-h-120"
              />
              <Dropdown
                title=""
                placeholder="All Professional Titles"
                data={MEMBER_SEARCH_PROFESSIONAL_TITLE_OPTIONS}
                selectedObjst={criteria.professionalTitle?.name}
                seter={(option: DropdownOption) =>
                  onChange({ professionalTitle: option })
                }
                getid
                internalSearch={false}
                renderMenuInPortal
                maxHeight="max-h-120"
              />
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Dropdown
                title=""
                placeholder="Select Firm"
                data={firms}
                selectedObjst={criteria.firm?.name}
                seter={handleFirmSelect}
                getid
                internalSearch
                renderMenuInPortal
                maxHeight="max-h-120"
                disabled={hasRegions || hasLocations}
              />
              <KeywordField
                value={criteria.keyword}
                onChange={(keyword) => onChange({ keyword })}
              />
            </div>

            <RadioGroup
              title="Service Type:"
              showTitle={false}
              name="serviceType"
              color="accent"
              value={selectedServiceType}
              options={SERVICE_TYPE_OPTIONS}
              onChange={(serviceType) => onChange({ serviceType })}
            />

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <Dropdown
                title=""
                placeholder="Select Practice Industry"
                data={practiceIndustries}
                selectedObjst={criteria.practiceIndustry?.name}
                seter={(option: DropdownOption) =>
                  onChange({ practiceIndustry: option })
                }
                getid
                internalSearch
                renderMenuInPortal
                maxHeight="max-h-120"
              />
              <DDMultiple
                title=""
                placeholder="Select Region"
                objects={regions}
                selectedObjst={selectedRegions}
                seter={(next) => {
                  const options = typeof next === "function" ? next(selectedRegions) : next;
                  onChange({
                    regions: toMemberOptions(options),
                    locations: [],
                    firm: undefined,
                    officeLocation: undefined,
                  });
                }}
                internalSearch
                disabled={hasFirm || hasLocations}
              />
              <DDMultiple
                title=""
                placeholder="Select Location"
                objects={locations}
                selectedObjst={selectedLocations}
                seter={(next) => {
                  const options = typeof next === "function" ? next(selectedLocations) : next;
                  onChange({
                    locations: toMemberOptions(options),
                    regions: [],
                    firm: undefined,
                    officeLocation: undefined,
                  });
                }}
                internalSearch
                disabled={hasFirm || hasRegions}
              />
              <Dropdown
                key={criteria.firm?.id ?? "all-firms"}
                title=""
                placeholder={officeFieldPlaceholder}
                data={officeLocations}
                selectedObjst={criteria.officeLocation?.name}
                seter={(option: DropdownOption) =>
                  onChange({ officeLocation: option })
                }
                getid
                internalSearch
                renderMenuInPortal
                maxHeight="max-h-120"
                disabled={officeDisabled}
              />
            </div>
          </>
        )}

        <div className="flex justify-end pt-2">
          <LegacyButton
            text="Submit"
            type="submit"
            color="accent"
            style="filled"
            size="large"
            content="textIcon"
            icon={<ChevronRight className="h-5 w-5 fill-white" />}
            className="!h-11 !min-h-11 !max-h-11 !w-[290px] !min-w-[290px] !flex-none !justify-center !px-5 !py-3 !text-base"
            onClick={onSearch}
          />
        </div>
      </div>
    </form>
  );
};

export default AdvancedMemberSearchForm;
